import {
  PayrollActionType,
  PayrollCycle,
  PayrollReportRowDto,
  PayrollReportView,
  isDefined,
} from '@zorro/clients';
import { VALIDATION_MESSAGES, stringComparator } from '@zorro/common-utils';
import {
  formatToFixedFloat,
  parseDate,
  parseDateISOThenFormatEnLocale,
  parseDateTimeISOThenFormatDateTimeEnLocale,
  parseDateUnknown,
} from '@zorro/shared/formatters';
import { DeepReadonly, employerFamilyUnitLabelConfig } from '@zorro/types';
import { screamingSnakeCaseToText } from '@zorro/web/ui-utils';
import { DataTableSortStatus } from 'mantine-datatable';
import { $enum } from 'ts-enum-util';
import * as yup from 'yup';

import { Filter } from '../Filters';
import { AvailableView } from '../SearchableDataTable';
import {
  DisplayActionType,
  PayrollReportMode,
  PayrollRow,
  PayrollViewToDataMap,
} from './PayrollReportTypes';

const actionTypeToDisplayMap: Record<PayrollActionType, DisplayActionType> = {
  [PayrollActionType.CHANGE_AMOUNTS]: DisplayActionType.CHANGE_AMOUNTS,
  [PayrollActionType.CHANGE_AMOUNTS_MID_MONTH]:
    DisplayActionType.CHANGE_AMOUNTS,
  [PayrollActionType.ADD_AMOUNTS]: DisplayActionType.ADD_AMOUNTS,
  [PayrollActionType.REMOVE_AMOUNTS]: DisplayActionType.REMOVE_AMOUNTS,
  [PayrollActionType.REMOVE_AMOUNTS_MID_MONTH]:
    DisplayActionType.REMOVE_AMOUNTS,
  [PayrollActionType.RETROACTIVE_CHANGE]: DisplayActionType.RETROACTIVE_CHANGE,
};

function toHsaEligibilityLabel(isHsaEligible?: boolean | null): string {
  if (!isDefined(isHsaEligible)) {
    return '-';
  }
  return isHsaEligible ? 'Yes' : 'No';
}

export const mapPayrollReportRowDtoToRows = (
  employees: PayrollReportRowDto[]
): PayrollRow[] => {
  return employees.map((row) => {
    return {
      // Employee details
      firstName: row.firstName,
      lastName: row.lastName,
      employeeId: row.id,
      idFromEmployer: row.idFromEmployer ?? '-',
      subEntity: row.subEntity ?? '-',
      subEntity2: row.subEntity2 ?? '-',
      email: row.email,
      dateOfBirth: parseDateISOThenFormatEnLocale(row.dateOfBirth),
      state: row.state,
      employeeClass: row.employeeClass ?? '-',
      terminationDate: row.terminationDate
        ? parseDateISOThenFormatEnLocale(row.terminationDate)
        : '-',

      // Payroll summary
      actionType: row.actionType
        ? actionTypeToDisplayMap[row.actionType]
        : null,
      deduction: formatToFixedFloat(row.deduction)!,
      reimbursement: formatToFixedFloat(row.reimbursement)!,
      employerContribution: formatToFixedFloat(row.employerContribution)!,
      effectiveFrom: parseDateISOThenFormatEnLocale(row.effectiveFrom),
      effectiveUntil: parseDateISOThenFormatEnLocale(row.effectiveUntil),
      lastUpdate: row.lastUpdate
        ? parseDateTimeISOThenFormatDateTimeEnLocale(row.lastUpdate)
        : '-',

      // Medical plan details
      medicalPlanType: row.familyUnit
        ? employerFamilyUnitLabelConfig[row.familyUnit]
        : '-',
      medicalPlanCarrier: row.carrierName,
      medicalPlanName: row.planName,
      medicalPlanPremium: formatToFixedFloat(row.premium),
      medicalPlanEmployerAllowance: formatToFixedFloat(
        row.employerMonthlyContribution
      ),
      medicalPlanEmployeeContribution: formatToFixedFloat(
        row.employeeMonthlyContribution
      ),
      medicalPlanEmployeeSelfPayment: formatToFixedFloat(row.selfPayAmount),
      planMarket: row.planMarket
        ? screamingSnakeCaseToText(row.planMarket)
        : '-',
      isHsaEligible: toHsaEligibilityLabel(row.isHsaEligible),
      onboardingPeriodId: row.onboardingPeriodId,

      // Payroll summary for one time correction
      shouldShowPayrollForOneTimeCorrection: isDefined(row.oneTimeDeduction),

      oneTimeDeduction: formatToFixedFloat(row.oneTimeDeduction)!,
      oneTimeReimbursement: formatToFixedFloat(row.oneTimeReimbursement)!,
      oneTimeEmployerContribution: formatToFixedFloat(
        row.oneTimeEmployerContribution
      )!,
      oneTimeEffectiveFrom: row.oneTimeEffectiveFrom
        ? parseDateISOThenFormatEnLocale(row.oneTimeEffectiveFrom)
        : null,
      oneTimeEffectiveUntil: row.oneTimeEffectiveUntil
        ? parseDateISOThenFormatEnLocale(row.oneTimeEffectiveUntil)
        : null,
      oneTimeLastUpdate: row.oneTimeLastUpdate
        ? parseDateTimeISOThenFormatDateTimeEnLocale(row.oneTimeLastUpdate)
        : '-',

      // Medical plan details for one time correction
      shouldShowEnrollmentForOneTimeCorrection: isDefined(row.oneTimePlanName),

      oneTimeMedicalPlanType: row.oneTimeFamilyUnit
        ? employerFamilyUnitLabelConfig[row.oneTimeFamilyUnit]
        : '-',
      oneTimeMedicalPlanCarrier: row.oneTimeCarrierName ?? null,
      oneTimeMedicalPlanName: row.oneTimePlanName ?? null,
      oneTimeMedicalPlanPremium: formatToFixedFloat(row.oneTimePremium),
      oneTimeMedicalPlanEmployerAllowance: formatToFixedFloat(
        row.oneTimeEmployerMonthlyContribution
      ),
      oneTimeMedicalPlanEmployeeContribution: formatToFixedFloat(
        row.oneTimeEmployeeMonthlyContribution
      ),
      oneTimeMedicalPlanEmployeeSelfPayment: formatToFixedFloat(
        row.oneTimeSelfPayAmount
      ),
      oneTimePlanMarket: row.oneTimePlanMarket
        ? screamingSnakeCaseToText(row.oneTimePlanMarket)
        : '-',
      oneTimeIsHsaEligible: toHsaEligibilityLabel(row.oneTimeIsHsaEligible),
    };
  });
};

export const payrollReportColumns = [
  { label: 'First name', value: 'firstName' },
  { label: 'Last name', value: 'lastName' },
  { label: 'ID', value: 'idFromEmployer' },
  { label: 'Sub-entity', value: 'subEntity' },
  { label: 'Sub-entity 2', value: 'subEntity2' },
  { label: 'Email', value: 'email' },
  { label: 'Date of birth', value: 'dateOfBirth' },
  { label: 'State', value: 'state' },
  { label: 'Class', value: 'employeeClass' },
  { label: 'Termination Date', value: 'terminationDate' },
  { label: 'Action Type', value: 'actionType' },
  {
    label: 'Deduction',
    value: 'deduction',
  },
  {
    label: 'Reimbursement',
    value: 'reimbursement',
  },
  {
    label: 'Employer contribution',
    value: 'employerContribution',
  },
  { label: 'Effective From', value: 'effectiveFrom' },
  { label: 'Effective Until', value: 'effectiveUntil' },
  { label: 'Last Update', value: 'lastUpdate' },
  { label: 'Family unit', value: 'medicalPlanType' },
  { label: 'Carrier', value: 'medicalPlanCarrier' },
  { label: 'Plan name', value: 'medicalPlanName' },
  { label: 'Monthly premium', value: 'medicalPlanPremium' },
  {
    label: 'Monthly employer contribution',
    value: 'medicalPlanEmployerAllowance',
  },
  {
    label: 'Monthly employee contribution',
    value: 'medicalPlanEmployeeContribution',
  },
  {
    label: 'Monthly employee self payment',
    value: 'medicalPlanEmployeeSelfPayment',
  },
  { label: 'Plan market', value: 'planMarket' },
  { label: 'HSA eligible', value: 'isHsaEligible' },
];

export function getColumnLabel(value: string): string {
  return (
    payrollReportColumns.find((column) => column.value === value)?.label || ''
  );
}

export const payrollFormSchema = yup.object({
  reportMode: yup
    .mixed<PayrollReportMode>()
    .oneOf(
      Object.values(PayrollReportMode),
      VALIDATION_MESSAGES.reportModeRequired
    )
    .required(VALIDATION_MESSAGES.reportModeRequired),
  payrollCycle: yup
    .mixed<PayrollCycle>()
    .oneOf(
      Object.values(PayrollCycle),
      VALIDATION_MESSAGES.payrollCycleRequired
    )
    .required(VALIDATION_MESSAGES.payrollCycleRequired),
  payPeriodStartDate: yup
    .date()
    .required(VALIDATION_MESSAGES.payPeriodStartDateRequired)
    .defined(),
  payPeriodEndDate: yup
    .date()
    .required(VALIDATION_MESSAGES.payPeriodEndDateRequired)
    .nullable()
    .test('required', VALIDATION_MESSAGES.payPeriodEndDateRequired, (value) =>
      isDefined(value)
    ),
  indicateChangesSince: yup
    .date()
    .required(VALIDATION_MESSAGES.indicateChangesSinceRequired)
    .nullable(),
});

export type PayrollFormData = yup.InferType<typeof payrollFormSchema>;

export function getFilteredSortedSearchedRecordsForPayroll(
  employees: PayrollRow[] | undefined,
  filters: Filter<PayrollRow>[],
  sortStatus: DataTableSortStatus,
  searchValue: string
): PayrollRow[] | undefined {
  if (!employees) return undefined;
  const filtered = employees
    .filter((employee) => {
      let keep = true;
      filters.forEach((filter) => {
        if (!filter.filterCallback(employee)) {
          keep = false;
        }
      });
      return keep;
    })
    .filter((employee) => {
      const lowerCaseValue = searchValue.toLowerCase();
      return Object.entries(employee).some(([field, value]) => {
        if (field === 'actionType' && value) {
          const actionTypeLabel =
            payrollActionTypeLabelConfig[value as DisplayActionType];
          return actionTypeLabel.toLowerCase().includes(lowerCaseValue);
        }
        return String(value).toLowerCase().includes(lowerCaseValue);
      });
    });

  const dataToSort = filtered.sort((firstRow, secondRow) => {
    const first = firstRow[sortStatus.columnAccessor as keyof PayrollRow];
    const second = secondRow[sortStatus.columnAccessor as keyof PayrollRow];

    if (typeof first === 'string' && typeof second === 'string') {
      const firstDate = parseDateUnknown(first);
      const secondDate = parseDateUnknown(second);
      if (firstDate.isValid() && secondDate.isValid()) {
        return firstDate.diff(secondDate);
      }
    }

    return typeof first === 'number' && typeof second === 'number'
      ? first - second
      : stringComparator(first?.toString(), second?.toString());
  });

  return sortStatus.direction === 'asc' ? dataToSort : dataToSort.reverse();
}

export function getAvailableViewsForPayroll(
  viewToFilteredDataMap: PayrollViewToDataMap,
  indicateChangesSince: string
) {
  return [
    {
      label: `Actions required since ${indicateChangesSince}`,
      value: PayrollReportView.ACTIONS_REQUIRED_SINCE,
      count:
        viewToFilteredDataMap.get(PayrollReportView.ACTIONS_REQUIRED_SINCE)
          ?.data?.length || 0,
    },
    {
      label: 'Full pay period report',
      value: PayrollReportView.FULL_PAY_PERIOD,
      count:
        viewToFilteredDataMap.get(PayrollReportView.FULL_PAY_PERIOD)?.data
          ?.length || 0,
    },
  ];
}

export function getCountForView(
  availableViews: AvailableView[],
  viewValue: string
): number {
  return availableViews.find((view) => view.value === viewValue)?.count || 0;
}

export function calculatePayPeriodEndDate(
  payrollCycle: PayrollCycle,
  startDate: Date
): Date | null {
  const parsedStartDate = parseDate(startDate);

  return $enum.visitValue(payrollCycle).with({
    [PayrollCycle.WEEKLY]: () => parsedStartDate.add(6, 'day').toDate(),
    [PayrollCycle.WEEKLY_DEDUCTIONS]: () =>
      parsedStartDate.add(6, 'day').toDate(),
    [PayrollCycle.BIWEEKLY]: () => parsedStartDate.add(13, 'day').toDate(),
    [PayrollCycle.BIWEEKLY_DEDUCTIONS]: () =>
      parsedStartDate.add(13, 'day').toDate(),
    [PayrollCycle.MONTHLY]: () =>
      parsedStartDate.add(1, 'month').subtract(1, 'day').toDate(),
    [PayrollCycle.SEMI_MONTHLY]: () => null,
  });
}

export const payrollActionTypeLabelConfig: DeepReadonly<{
  [key in DisplayActionType]: string;
}> = {
  [DisplayActionType.CHANGE_AMOUNTS]: 'Change amounts',
  [DisplayActionType.ADD_AMOUNTS]: 'Add amounts',
  [DisplayActionType.REMOVE_AMOUNTS]: 'Remove amounts',
  [DisplayActionType.RETROACTIVE_CHANGE]: 'Retroactive update',
  [DisplayActionType.ONE_TIME_CORRECTION]: 'One-time correction',
};

function coalesceDisplayValuesForCsv(row: PayrollRow): PayrollRow {
  return {
    ...row,
    medicalPlanPremium: row.medicalPlanPremium ?? 0,
    medicalPlanEmployerAllowance: row.medicalPlanEmployerAllowance ?? 0,
    medicalPlanEmployeeContribution: row.medicalPlanEmployeeContribution ?? 0,
    medicalPlanEmployeeSelfPayment: row.medicalPlanEmployeeSelfPayment ?? 0,
  };
}

export function prepareRecordsForExportToCsv(
  records: PayrollRow[]
): PayrollRow[] {
  const recordsForCsv: PayrollRow[] = [];
  for (const row of records) {
    recordsForCsv.push(coalesceDisplayValuesForCsv(row));
    if (row.shouldShowPayrollForOneTimeCorrection) {
      const oneTimeCorrectionRow: PayrollRow = {
        ...row,
        actionType: DisplayActionType.ONE_TIME_CORRECTION,
        deduction: row.oneTimeDeduction!,
        reimbursement: row.oneTimeReimbursement!,
        employerContribution: row.oneTimeEmployerContribution!,
        effectiveFrom: row.oneTimeEffectiveFrom ?? row.effectiveFrom,
        effectiveUntil: row.oneTimeEffectiveUntil ?? row.effectiveUntil,
        lastUpdate: row.oneTimeLastUpdate,
        medicalPlanType: row.oneTimeMedicalPlanType ?? row.medicalPlanType,
        medicalPlanCarrier:
          row.oneTimeMedicalPlanCarrier ?? row.medicalPlanCarrier,
        medicalPlanName: row.oneTimeMedicalPlanName ?? row.medicalPlanName,
        medicalPlanPremium:
          row.oneTimeMedicalPlanPremium ?? row.medicalPlanPremium,
        medicalPlanEmployerAllowance:
          row.oneTimeMedicalPlanEmployerAllowance ??
          row.medicalPlanEmployerAllowance,
        medicalPlanEmployeeContribution:
          row.oneTimeMedicalPlanEmployeeContribution ??
          row.medicalPlanEmployeeContribution,
        medicalPlanEmployeeSelfPayment:
          row.oneTimeMedicalPlanEmployeeSelfPayment ??
          row.medicalPlanEmployeeSelfPayment,
        planMarket: row.oneTimePlanMarket ?? row.planMarket,
        isHsaEligible: row.shouldShowEnrollmentForOneTimeCorrection
          ? row.oneTimeIsHsaEligible
          : row.isHsaEligible,
      };
      recordsForCsv.push(coalesceDisplayValuesForCsv(oneTimeCorrectionRow));
    }
  }
  return recordsForCsv;
}
